import React, { forwardRef, type MouseEvent, type CSSProperties } from "react";

import { useLazyImageContext } from "../../layout/LazyImageContext";

interface RtrImageProps {
  // HTML "img" attributes
  "alt"?: string;
  "crossOrigin"?: "" | "anonymous" | "use-credentials";
  "decoding"?: "async" | "auto" | "sync";
  "height"?: string | number;
  "loading"?: "" | "eager" | "lazy";
  "referrerPolicy"?:
    | ""
    | "no-referrer"
    | "no-referrer-when-downgrade"
    | "origin"
    | "origin-when-cross-origin"
    | "same-origin"
    | "strict-origin"
    | "strict-origin-when-cross-origin"
    | "unsafe-url";
  "sizes"?: string;
  "src"?: string;
  "srcSet"?: string;
  "useMap"?: string;
  "width"?: string | number;
  // Some generic HTML attributes
  "className"?: string;
  "onClick"?: (event: MouseEvent<HTMLImageElement>) => void;
  "onError"?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  "style"?: CSSProperties;
  // Other
  "itemProp"?: string;
  "fetchpriority"?: string;
  "draggable"?: boolean;
  "data-test-id"?: string;
  "onLoad"?: () => void;
}

/**
 * This wrapper component passes all props through to the image
 */
const RtrImage = forwardRef<HTMLImageElement, RtrImageProps>(
  (
    {
      alt,
      className,
      crossOrigin,
      decoding,
      height,
      loading,
      referrerPolicy,
      sizes,
      src,
      srcSet,
      useMap,
      width,
      onClick,
      onError,
      style,
      itemProp,
      fetchpriority,
      draggable,
      "data-test-id": dataTestId = "image",
      onLoad = () => {},
    },
    ref
  ) => {
    const lazyLoadImage = useLazyImageContext();

    const getFetchPriority = (): "auto" | "high" | "low" | undefined => {
      if (!fetchpriority) return;

      switch (fetchpriority.toUpperCase()) {
        case "AUTO":
          return "auto";
        case "HIGH":
          return "high";
        case "LOW":
          return "low";
      }
    };

    let loadingStrategy: "" | "eager" | "lazy" = "eager";
    if (loading) {
      loadingStrategy = loading;
    } else if (lazyLoadImage) {
      loadingStrategy = "lazy";
    }
    // eslint-disable-next-line react/jsx-props-no-spreading, react/forbid-elements
    return (
      <img
        alt={alt}
        className={className}
        crossOrigin={crossOrigin}
        decoding={decoding}
        height={height}
        loading={loadingStrategy}
        referrerPolicy={referrerPolicy}
        sizes={sizes}
        src={src}
        srcSet={srcSet}
        onError={onError}
        useMap={useMap}
        width={width}
        onClick={onClick}
        style={style}
        itemProp={itemProp}
        ref={ref}
        fetchPriority={getFetchPriority()}
        draggable={draggable}
        data-test-id={dataTestId}
        onLoad={onLoad}
      />
    );
  }
);

RtrImage.displayName = "RtrImage";

export default RtrImage;
